import { Component, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  LOGGEDINUSER, UserAgentService, User, AppInitializerService, AppService, AmplitudeEventsService,
  AmplitudeProperties, AmplitudeService, CannyService, GtmService,
  NavigationHelper, FeatureFlags, LaunchDarklyService,
} from '@lc/core';
import { Router } from '@angular/router';
import {
  filter, tap, withLatestFrom, catchError, firstValueFrom, Observable,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'lc-agent-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})

export class AppComponent {
  private appInitializer = inject(AppInitializerService);
  private amplitudeEventsService = inject(AmplitudeEventsService);
  private amplitudeService = inject(AmplitudeService);
  private gtmService = inject(GtmService);
  private cannyService = inject(CannyService);
  private launchDarklyService = inject(LaunchDarklyService);

  omniChat$: Observable<Boolean>;
  private readonly GTAG_MAP = new Map([
    ['prod', 'GTM-5C5WLC2'],
    ['uat', 'GTM-K6MXBZ4'],
    ['local', 'GTM-W8JCFCV'],
  ]);

  constructor() {
    const { appInitializer } = this;
    const store = inject<Store<any>>(Store);
    const router = inject(Router);
    const userAgentService = inject(UserAgentService);

    userAgentService.doBrowserCheck();
    appInitializer.initializeKeys('dc-user-app', AppService.productSuiteName);

    // Listen for router changes and notify the gtag service when the route changes
    const user$: Observable<User> = store.select(LOGGEDINUSER).pipe(
      filter((user: User) => !!user?.profile),
    );

    // Set the canny SSO after user logged in
    firstValueFrom(user$)
      .then(() => {
        this.omniChat$ = this.launchDarklyService.getFeature$(FeatureFlags.OMNI_CHAT, false);
        this.cannyService.getCannySSO$().toPromise();
      });

    // Listen for router changes and notify the gtag service when the route changes
    NavigationHelper.urlChanged$(router)
      .pipe(
        tap((url) => this.appInitializer.refreshNotifications(url)),
        withLatestFrom(user$, this.amplitudeEventsService.orderContext$),
        tap(([url, user, orderContext]) => {
          const userProperties = this.getUserProperties(user);
          const event = {
            event: 'virtualPageview',
            page_path: url,
            ...userProperties,
          };

          this.gtmService.pushEvent(this.GTAG_MAP, event);
          this.amplitudeService.trackEvent(
            event.event,
            user.oktaId,
            userProperties,
            { page_path: event.page_path, ...orderContext },
          );
        }),
        catchError((err) => { throw new Error(err); }),
        takeUntilDestroyed(),
      ).subscribe();

    this.amplitudeEventsService.events$
      .pipe(
        withLatestFrom(user$),
        tap(([event, user]) => this.amplitudeService.trackEvent(
          event.eventType,
          user.oktaId,
          this.getUserProperties(user),
          event.properties,
        )),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private getUserProperties(user: User): AmplitudeProperties {
    const auditCreatedDate = user.profile.audit?.createdAt;
    const createdAt = auditCreatedDate
      ? Math.round(new Date(auditCreatedDate).getTime() / 1000)
      : Math.round(new Date().getTime() / 1000);

    return {
      country: 'US',
      userType: 'Agent',
      oktaID: user.oktaId,
      personMDMID: user.personMasterId || '',
      companyMDMID: user.profile.office?.company?.companyMasterId || '',
      companyName: user.profile.office?.company?.companyName || '',
      metroID: user.profile.office?.metro?.metroNumber || '',
      metroName: user.profile.office?.metro?.metroName || '',
      officeID: user.profile.office?.officeCode || '',
      officeName: user.profile.office?.address?.name || '',
      emailAddress: user.profile.preferredEmail || 'email',
      createdAt,
      brand: user.profile.office?.company?.brandCode || '',
    };
  }
}
